import React, { useEffect, useState } from "react";
import { Alert, Snackbar } from "@mui/material";
import { subscribeToGlobalErrors } from "../../utils/errorReporting";

const MAX_CHARS = 120;

const truncate = (message: string): string =>
  message.length > MAX_CHARS ? `${message.slice(0, MAX_CHARS).trimEnd()}…` : message;

/**
 * Informational toast for uncaught errors / unhandled rejections picked up by
 * `installGlobalErrorHandlers`. It never unmounts anything; mount it once.
 */
const GlobalErrorToast: React.FC = () => {
  const [toast, setToast] = useState<{ message: string; seq: number } | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(
    () =>
      subscribeToGlobalErrors((message) => {
        // A new seq remounts the Snackbar so its auto-hide timer restarts.
        setToast((prev) => ({ message, seq: (prev?.seq ?? 0) + 1 }));
        setOpen(true);
      }),
    [],
  );

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  if (!toast) return null;

  return (
    <Snackbar
      key={toast.seq}
      open={open}
      autoHideDuration={8000}
      onClose={handleClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    >
      <Alert severity="error" onClose={handleClose} sx={{ width: "100%" }}>
        Something went wrong: {truncate(toast.message)}
      </Alert>
    </Snackbar>
  );
};

export default GlobalErrorToast;
